import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import MultiImageUploader from '../components/MultiImageUploader';

const PRICING_KEYS = [
  { value: 'shared', label: 'Compartido' },
  { value: 'standard', label: 'Privado Estándar' },
  { value: 'tahoe', label: 'Privado Tahoe' },
  { value: 'suburban', label: 'Privado Suburban' },
  { value: 'limousine', label: 'Limousine' },
  { value: 'group', label: 'Grupal (9-30 pax)' },
];

const empty = {
  name: '', name_en: '', capacity: '',
  description: '', description_en: '',
  image: '', features: [], pricing_key: 'standard',
};

const AdminVehicles = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [featuresText, setFeaturesText] = useState('');

  const load = () => api.get('/vehicles').then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty, features: [] });
    setFeaturesText('');
    setOpen(true);
  };

  const openEdit = (it) => {
    const features = Array.isArray(it.features) ? it.features : [];
    setEditing(it);
    setForm({ ...it, features, image: it.image || '' });
    setFeaturesText(features.join(', '));
    setOpen(true);
  };

  const save = async () => {
    try {
      const features = featuresText.split(',').map(s => s.trim()).filter(Boolean);
      const payload = { ...form, features };
      if (editing) await api.put(`/vehicles/${editing.id}`, payload);
      else await api.post('/vehicles', payload);
      toast.success('Vehículo guardado');
      setOpen(false);
      load();
    } catch {
      toast.error('Error al guardar');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar este vehículo?')) return;
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success('Eliminado');
      load();
    } catch {
      toast.error('Error');
    }
  };

  // For uploader, we use a virtual gallery [image] since vehicle has only one image
  const galleryFromForm = form.image ? [form.image] : [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-black text-white">Vehículos</h1>
          <p className="text-white/60">Gestiona los vehículos disponibles para reservas</p>
        </div>
        <Button onClick={openNew} className="btn-orange border-0 gap-2">
          <Plus className="w-4 h-4" /> Nuevo Vehículo
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map(v => (
          <div key={v.id} className="glass-card rounded-xl overflow-hidden">
            <div className="relative h-44 overflow-hidden">
              {v.image && <img src={v.image} alt={v.name} className="w-full h-full object-cover" />}
              <div className="absolute top-3 left-3 bg-[#ff6b1a] text-white px-2 py-0.5 rounded text-[10px] font-bold uppercase">
                {PRICING_KEYS.find(k => k.value === v.pricing_key)?.label || v.pricing_key}
              </div>
            </div>
            <div className="p-5">
              <h3 className="font-bold text-white mb-1">{v.name}</h3>
              <div className="flex items-center gap-1 text-xs text-white/60 mb-3">
                <Users className="w-3.5 h-3.5 text-[#ff6b1a]" /> {v.capacity}
              </div>
              <p className="text-white/60 text-xs mb-3 line-clamp-2">{v.description}</p>
              {v.features?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {v.features.slice(0, 3).map((f) => (
                    <span key={f} className="text-[10px] bg-white/5 text-white/70 px-2 py-0.5 rounded border border-white/10">{f}</span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={() => openEdit(v)} variant="outline" size="sm" className="flex-1 border-white/15 text-white hover:bg-white/5">
                  <Edit className="w-3.5 h-3.5 mr-1" /> Editar
                </Button>
                <Button onClick={() => remove(v.id)} variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0f0f0f] border-white/10 text-white max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar' : 'Nuevo'} Vehículo</DialogTitle>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Nombre (ES)</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/40 border-white/10" />
            </div>
            <div>
              <Label>Nombre (EN)</Label>
              <Input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} className="bg-black/40 border-white/10" />
            </div>
            <div>
              <Label>Capacidad</Label>
              <Input value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} placeholder="1-8 pasajeros" className="bg-black/40 border-white/10" />
            </div>
            <div>
              <Label>Tipo de tarifa</Label>
              <Select value={form.pricing_key} onValueChange={(v) => setForm({ ...form, pricing_key: v })}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0f0f0f] border-white/10">
                  {PRICING_KEYS.map(k => <SelectItem key={k.value} value={k.value} className="text-white">{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Descripción (ES)</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-black/40 border-white/10" />
            </div>
            <div className="md:col-span-2">
              <Label>Descripción (EN)</Label>
              <Textarea rows={3} value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} className="bg-black/40 border-white/10" />
            </div>
            <div className="md:col-span-2">
              <Label>Características (separadas por coma)</Label>
              <Input value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} placeholder="Aire Acondicionado, WiFi, Chofer Profesional" className="bg-black/40 border-white/10" />
            </div>
            <div className="md:col-span-2">
              <Label>Imagen del Vehículo</Label>
              <MultiImageUploader
                value={galleryFromForm}
                onChange={(urls) => setForm({ ...form, image: urls[0] || '' })}
                folder="tts/vehicles"
              />
              <p className="text-[11px] text-white/40 mt-1.5">Solo se usa la primera imagen como portada del vehículo.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-white/15 text-white">Cancelar</Button>
            <Button onClick={save} className="btn-orange border-0">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminVehicles;
